import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faSpinner, faMugHot } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { Button } from '../../ui';
import {
    listPendingBrands,
    approveBrand,
    rejectBrand,
    approveBrandType,
    rejectBrandType,
    PendingBrand,
} from '../coffee_card_web/api/coffeeApi';
import { SupermarketFormat, SupermarketType } from '../coffee_card_web/CoffeeCardWeb/types';

function formatDate(iso?: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString();
}

// Render the milk/sugar/iced flags of a product type as compact chips.
function formatChips(format?: SupermarketFormat): string[] {
    if (!format) return [];
    const chips: string[] = [];
    if (format.milk) chips.push('milk');
    if (format.sugar) chips.push('sugar');
    if (format.iced) chips.push('iced');
    return chips;
}

// Each action keys a busy row by a stable id so only that button spins.
// Brands key on the brand _id; types key on `${brandId}:${type.key}`.
const typeActingKey = (brandId: string, key: string) => `${brandId}:${key}`;

const CoffeeBrandsAdmin: React.FC = () => {
    const [brands, setBrands] = useState<PendingBrand[]>([]);
    const [loading, setLoading] = useState(true);
    const [actingKey, setActingKey] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const rows = await listPendingBrands();
            setBrands(rows);
        } catch {
            toast.error('Failed to load pending brands');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const onApproveBrand = async (id: string) => {
        setActingKey(id);
        try {
            await approveBrand(id);
            toast.success('Brand approved');
            await fetchData();
        } catch {
            toast.error('Failed to approve brand');
        } finally {
            setActingKey(null);
        }
    };

    const onRejectBrand = async (id: string) => {
        setActingKey(id);
        try {
            await rejectBrand(id);
            toast.success('Brand rejected');
            await fetchData();
        } catch (err: any) {
            // Reject 409s when the brand already has ratings attached to it.
            const status = err?.response?.status;
            if (status === 409) {
                toast.error('Cannot reject — this brand already has ratings');
            } else {
                toast.error('Failed to reject brand');
            }
        } finally {
            setActingKey(null);
        }
    };

    const onApproveType = async (brandId: string, type: SupermarketType) => {
        const key = typeActingKey(brandId, type.key);
        setActingKey(key);
        try {
            await approveBrandType(brandId, type.key);
            toast.success('Type approved');
            await fetchData();
        } catch {
            toast.error('Failed to approve type');
        } finally {
            setActingKey(null);
        }
    };

    const onRejectType = async (brandId: string, type: SupermarketType) => {
        const key = typeActingKey(brandId, type.key);
        setActingKey(key);
        try {
            await rejectBrandType(brandId, type.key);
            toast.success('Type rejected');
            await fetchData();
        } catch {
            toast.error('Failed to reject type');
        } finally {
            setActingKey(null);
        }
    };

    return (
        <div className="container mx-auto p-4 max-w-3xl">
            <h1 className="text-2xl font-bold mb-1 flex items-center gap-2">
                <FontAwesomeIcon icon={faMugHot} className="text-muted-foreground" />
                Coffee Brands
            </h1>
            <p className="text-sm text-muted-foreground mb-4">
                Review user-submitted supermarket brands and product types.
            </p>

            {loading ? (
                <div className="flex items-center justify-center py-8">
                    <FontAwesomeIcon icon={faSpinner} className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            ) : brands.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No pending brands or types</p>
            ) : (
                <div className="space-y-2">
                    {brands.map((brand) => {
                        const brandActing = actingKey === brand._id;
                        const submitter = brand.createdBy;

                        return (
                            <div
                                key={brand._id}
                                className="p-3 border border-ink/15 rounded-lg bg-paper space-y-3"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <div className="font-medium flex items-center gap-2">
                                            {brand.brandPending && (
                                                <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800 flex-shrink-0">
                                                    pending brand
                                                </span>
                                            )}
                                            {brand.name}
                                        </div>
                                        {submitter && (
                                            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                                {submitter.userEmoji && (
                                                    <span className="flex-shrink-0">{submitter.userEmoji}</span>
                                                )}
                                                {submitter.username}
                                            </div>
                                        )}
                                        {brand.createdAt && (
                                            <div className="text-xs text-muted-foreground mt-1">
                                                submitted {formatDate(brand.createdAt)}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Brand-level approve/reject — only when the brand itself is pending. */}
                                {brand.brandPending && (
                                    <div className="flex flex-wrap gap-2 pt-1 border-t border-ink/10">
                                        <Button
                                            size="sm"
                                            onClick={() => onApproveBrand(brand._id)}
                                            disabled={brandActing}
                                        >
                                            {brandActing ? (
                                                <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1" />
                                            ) : (
                                                <FontAwesomeIcon icon={faCheck} className="mr-1" />
                                            )}
                                            Approve brand
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="destructive"
                                            onClick={() => onRejectBrand(brand._id)}
                                            disabled={brandActing}
                                        >
                                            <FontAwesomeIcon icon={faTimes} className="mr-1" />
                                            Reject brand
                                        </Button>
                                    </div>
                                )}

                                {/* Pending product types under this brand. */}
                                {brand.pendingTypes.length > 0 && (
                                    <div className="space-y-2 pt-1 border-t border-ink/10">
                                        <div className="text-xs font-medium text-muted-foreground">
                                            Pending types
                                        </div>
                                        {brand.pendingTypes.map((type) => {
                                            const key = typeActingKey(brand._id, type.key);
                                            const typeActing = actingKey === key;
                                            const chips = formatChips(type.format);

                                            return (
                                                <div
                                                    key={type.key}
                                                    className="flex items-center justify-between gap-2 flex-wrap p-2 rounded bg-ink/5"
                                                >
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-sm font-medium">{type.name}</div>
                                                        {chips.length > 0 && (
                                                            <div className="flex flex-wrap gap-1 mt-1">
                                                                {chips.map((c) => (
                                                                    <span
                                                                        key={c}
                                                                        className="text-[11px] px-1.5 py-0.5 rounded bg-ink/10 text-muted-foreground"
                                                                    >
                                                                        {c}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex gap-2 flex-shrink-0">
                                                        <Button
                                                            size="sm"
                                                            onClick={() => onApproveType(brand._id, type)}
                                                            disabled={typeActing}
                                                        >
                                                            {typeActing ? (
                                                                <FontAwesomeIcon
                                                                    icon={faSpinner}
                                                                    className="animate-spin mr-1"
                                                                />
                                                            ) : (
                                                                <FontAwesomeIcon icon={faCheck} className="mr-1" />
                                                            )}
                                                            Approve
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="destructive"
                                                            onClick={() => onRejectType(brand._id, type)}
                                                            disabled={typeActing}
                                                        >
                                                            <FontAwesomeIcon icon={faTimes} className="mr-1" />
                                                            Reject
                                                        </Button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default CoffeeBrandsAdmin;
